import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { MAP_OVERRIDES } from '../../data/tours/sanJuanTourData';
import { TacticalCompass } from './TacticalCompass';

// Localized strings
const STRINGS = {
  en: {
    needHelp: "Need Help?",
    getDirections: "Get directions to your next destination",
    yourDestination: "Your Destination",
    directionsInGoogleMaps: "Directions in Google Maps",
    getTurnByTurn: "Get turn-by-turn directions",
    needHelpFinding: "Need Help Finding This Location?",
  },
  es: {
    needHelp: "¿Necesitas Ayuda?",
    getDirections: "Obtén direcciones a tu próximo destino",
    yourDestination: "Tu Destino",
    directionsInGoogleMaps: "Direcciones en Google Maps",
    getTurnByTurn: "Obtén indicaciones paso a paso",
    needHelpFinding: "¿Necesitas ayuda para encontrar esta ubicación?",
  }
};

/**
 * NavigationHelp Modal - Shows navigation options when user needs help
 * Displays: Google Maps
 */
export function NavigationHelp({ targetCoordinates, locationName, onClose, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  // Detect mission mode (Mexico City) vs regular mode (San Juan)
  const isMissionMode = !!targetCoordinates;

  // Get map overrides for this location
  const overrides = MAP_OVERRIDES[locationName] || {};

  // Generate Google Maps URL - prefer overrides, then coordinates, then location name search
  const googleMapsUrl = overrides.google
    ? overrides.google.includes(',') && !isNaN(overrides.google.split(',')[0])
      ? `https://www.google.com/maps/dir/?api=1&destination=${overrides.google.replace(/\s/g, '')}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(overrides.google)}`
    : targetCoordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${targetCoordinates.lat},${targetCoordinates.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationName + ', San Juan, Puerto Rico')}`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: isMissionMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)'
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: isMissionMode ? '#1e293b' : '#fffef9',
          border: isMissionMode ? '1px solid rgba(16, 185, 129, 0.3)' : '2px solid #DAA520',
          boxShadow: isMissionMode ? '0 0 40px rgba(16, 185, 129, 0.2)' : '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className={`text-xl font-bold ${isMissionMode ? 'text-white' : 'text-slate-900'}`}>
                {t.needHelp}
              </h2>
              <p className={`text-sm mt-1 ${isMissionMode ? 'text-gray-400' : 'text-slate-600'}`}>
                {t.getDirections}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{
                background: isMissionMode ? 'rgba(71, 85, 105, 0.5)' : '#f1f5f9',
                border: isMissionMode ? '1px solid rgba(71, 85, 105, 0.8)' : '1px solid #e2e8f0',
                color: isMissionMode ? '#ffffff' : '#64748b'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isMissionMode ? 'rgba(71, 85, 105, 0.7)' : '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isMissionMode ? 'rgba(71, 85, 105, 0.5)' : '#f1f5f9';
              }}
            >
              ✕
            </button>
          </div>

          {locationName && (
            <div
              className="mb-5 p-4 rounded-xl"
              style={{
                background: isMissionMode
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'linear-gradient(135deg, rgba(218, 165, 32, 0.1) 0%, rgba(255, 193, 37, 0.1) 100%)',
                border: isMissionMode ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(218, 165, 32, 0.3)'
              }}
            >
              <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
                isMissionMode ? 'text-emerald-400' : 'text-amber-700'
              }`}>
                {t.yourDestination}
              </div>
              <div className={`text-base font-bold ${isMissionMode ? 'text-white' : 'text-slate-900'}`}>
                {locationName}
              </div>
            </div>
          )}

          {/* Tactical Navigation - only show when coordinates are provided (mission mode) */}
          {targetCoordinates && (
            <div className="mb-5">
              <TacticalCompass
                targetCoordinates={targetCoordinates}
                locationName={locationName}
              />
            </div>
          )}

          <div className="space-y-3">
            {/* Google Maps */}
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full p-4 rounded-xl transition-all"
                style={{
                  background: isMissionMode ? 'rgba(71, 85, 105, 0.3)' : '#f8fafc',
                  border: isMissionMode ? '1px solid rgba(71, 85, 105, 0.5)' : '1px solid #e2e8f0',
                  textDecoration: 'none',
                  boxShadow: isMissionMode ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isMissionMode ? 'rgba(71, 85, 105, 0.5)' : '#f1f5f9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = isMissionMode ? '0 4px 12px rgba(16, 185, 129, 0.2)' : '0 4px 8px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isMissionMode ? 'rgba(71, 85, 105, 0.3)' : '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isMissionMode ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)';
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: isMissionMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(66, 133, 244, 0.1)',
                      border: isMissionMode ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(66, 133, 244, 0.3)'
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                        fill={isMissionMode ? '#10B981' : '#4285F4'}
                      />
                      <circle cx="12" cy="9" r="2.5" fill="white" />
                    </svg>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold mb-1 ${isMissionMode ? 'text-white' : 'text-slate-900'}`}>
                      {t.directionsInGoogleMaps}
                    </div>
                    <div className={`text-xs ${isMissionMode ? 'text-gray-400' : 'text-slate-500'}`}>
                      {t.getTurnByTurn}
                    </div>
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * NavigationHelpButton - Compact button to trigger navigation help modal
 */
export function NavigationHelpButton({ targetCoordinates, locationName, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const [showModal, setShowModal] = useState(false);

  if (!targetCoordinates) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full px-4 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
        style={{
          background: 'rgba(71, 85, 105, 0.3)',
          border: '1px solid rgba(71, 85, 105, 0.5)',
          color: '#ffffff'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(71, 85, 105, 0.3)';
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {t.needHelpFinding}
      </button>

      {showModal && (
        <NavigationHelp
          targetCoordinates={targetCoordinates}
          locationName={locationName}
          onClose={() => setShowModal(false)}
          lang={lang}
        />
      )}
    </>
  );
}
